import type { BookingToCheckoutParameters } from '@/domain/groups/api'
import type { PersonalDataFormInputPayload } from '@/domain/groups/types'
import type { APIBookingDraft, APIStepsTabs } from '@/scopes/checkout/infrastructure/api/types'
import type {
  BookingDraftConfigurationParameters,
  BookingDraftCouponCodeParameters,
  BookingDraftEngineErrorParameters,
  BookingDraftParameters,
  BookingStepsTabsParameters,
  ServiceMappers,
  Services,
  ServiceType,
} from '../../domain/types'

import type { BookingDraftResponseMapper } from './bookingDraftResponseMapper'
import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import defaultFetch from 'isomorphic-unfetch'
import { type BookingStepsResponseMapper, createBookingStepsResponseMapper } from './bookingStepsResponseMapper'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
    NEXT_PUBLIC_CHECKOUT_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  bookingDraftResponseMapper: BookingDraftResponseMapper
  bookingStepsResponseMapper: BookingStepsResponseMapper
  queryStringSerializer: QueryStringSerializer
  serviceMappers: ServiceMappers
}

export function createCheckoutAPIClient(
  dependencies: Partial<Dependencies> & Pick<Dependencies, 'bookingDraftResponseMapper'>,
) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
      NEXT_PUBLIC_CHECKOUT_API_BASE_URL: process.env.NEXT_PUBLIC_CHECKOUT_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer(),
    bookingDraftResponseMapper,
    bookingStepsResponseMapper = createBookingStepsResponseMapper(),
    serviceMappers,
  } = dependencies as Dependencies

  async function getBookingDraft(
    draftParameters: BookingDraftParameters,
    configParameters?: BookingDraftConfigurationParameters,
  ) {
    let bookingDraftResponseData: APIBookingDraft
    try {
      const url = buildBookingDraftEndpointUrl(draftParameters)
      const response = await fetch(url, { credentials: 'include' })
      if (!response.ok) {
        throw new Error(`Failed to fetch booking draft: ${response.status} ${response.statusText}`)
      }
      bookingDraftResponseData = await response.json()
    }
    catch (error) {
      bookingDraftResponseData = {
        error: {
          hasError: true,
          errorMessage: (error as Error).message,
          description: 'Booking not found',
        },
        draftId: '',
        services: [],
        participants: [],
        rooms: [],
        isRebooking: false,
        conditionalMessages: [],
      }
    }
    return bookingDraftResponseMapper.map(bookingDraftResponseData, configParameters?.configData)
  }

  async function getStepsTabs(draftParameters: BookingStepsTabsParameters) {
    const url = buildStepsTabsEndpointUrl(draftParameters)
    const response = await fetch(url)

    const responseData: APIStepsTabs = await response.json()

    return bookingStepsResponseMapper.map(responseData)
  }

  async function setServiceSelection<K extends ServiceType>(
    apiContext: BookingDraftParameters,
    serviceType: K,
    updatedService: Services[K],
  ) {
    const serviceMapper = serviceMappers[serviceType]
    const typeOfService = serviceMapper.getServiceTypeMapper()
    const serviceSelection = serviceMapper.mapSelection(updatedService)
    const url = buildChangeServiceEndpointUrl(apiContext)

    await fetch(url, {
      method: 'PATCH',
      body: JSON.stringify({
        id: updatedService.id,
        typeOfService,
        data: serviceSelection,
      }),
    })
  }

  async function setParticipantsInformation(
    apiContext: BookingDraftParameters,
    payload: { id: string, name: string, birthdate?: string }[],
  ) {
    const url = buildSetParticipantsBadgeEndpointUrl(apiContext)
    await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        participants: payload,
      }),
    })
  }

  async function setGroupParticipantsInformation(
    apiContext: BookingDraftParameters,
    payload: PersonalDataFormInputPayload[],
  ) {
    const url = buildSetParticipantsDetailsEndpointUrl(apiContext)
    return fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        participants: payload,
      }),
    })
  }

  async function setCouponCode(apiContext: BookingDraftCouponCodeParameters) {
    const url = buildSetCouponCodeEndpointUrl(apiContext)
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const responseData: APIBookingDraft = await response.json()

    return bookingDraftResponseMapper.map(responseData)
  }

  async function sendEngineErrorContactInfo(apiContext: BookingDraftEngineErrorParameters) {
    const url = buildEngineErrorContactInfoEndpointUrl(apiContext)
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerName: apiContext.customerName,
        customerEmail: apiContext.customerEmail,
        customerPhone: apiContext.customerPhone,
      }),
    })

    return response.ok
  }

  async function transformBookingToCheckout(apiContext: BookingToCheckoutParameters) {
    const url = buildTransformBookingToCheckoutEndpointUrl(apiContext)
    const response = await fetch(url, { credentials: 'include' })

    const responseData: APIBookingDraft = await response.json()

    return bookingDraftResponseMapper.map(responseData)
  }

  function buildBookingDraftEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/GetBookingDraft?${queryString}`
  }

  function buildStepsTabsEndpointUrl({ contextItemId, sid }: BookingStepsTabsParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/StepNavigation/GetStepTabs?${queryString}`
  }

  function buildChangeServiceEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/ChangeServices?${queryString}`
  }

  function buildSetParticipantsBadgeEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/SetParticipantBadges?${queryString}`
  }

  function buildSetCouponCodeEndpointUrl({ contextItemId, sid, couponCode }: BookingDraftCouponCodeParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '120000',
      couponCode,
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/ApplyCouponCode?${queryString}`
  }

  function buildEngineErrorContactInfoEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '120000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/SendInfoToContactCenter?${queryString}`
  }

  function buildSetParticipantsDetailsEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/SetParticipants?${queryString}`
  }

  function buildTransformBookingToCheckoutEndpointUrl({
    contextItemId,
    bookingNumber,
    groupUserId,
  }: BookingToCheckoutParameters) {
    const queryString = queryStringSerializer.serialize({
      bookingNumber,
      groupUserId,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingToCheckout/TransformBookingToCheckout?${queryString}`
  }

  return {
    getBookingDraft,
    setServiceSelection,
    getStepsTabs,
    setParticipantsInformation,
    setGroupParticipantsInformation,
    transformBookingToCheckout,
    setCouponCode,
    sendEngineErrorContactInfo,
  }
}
